import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { User, Home, Wrench, Camera, Heart, FileText } from "lucide-react";
import { trackLead, trackViewContent } from "../utils/metaPixel";
import logger from "../utils/logger";

// Import all questionnaire components
import {
  ProgressBar,
  NavigationButtons,
  ContactInfoStep,
  PropertyInfoStep,
  PropertyConditionStep,
  PropertyPhotosStep,
  SellerMotivationStep,
  FinalDetailsStep,
  SuccessScreen,
  validateStep
} from "../components/questionnaire";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const KI_LOGO = "/images/ki-logo.png";

const TOTAL_STEPS = 6;
const STEP_ICONS = [User, Home, Wrench, Camera, Heart, FileText];
const STEP_TITLES = ["Contact", "Property", "Condition", "Photos", "Motivation", "Details"];

// Initial form state
const INITIAL_FORM_DATA = {
  // Step 1 - Contact Info
  full_name: "",
  phone_number: "",
  email: "",
  
  // Step 2 - Property Info
  property_address: "",
  city: "",
  state: "",
  zip_code: "",
  property_type: "",
  occupancy_status: "",
  bedrooms: "",
  bathrooms: "",
  mortgage_status: "",
  
  // Step 2 - Vacant Land Specific Fields
  parcel_number: "",
  acreage: "",
  county: "",
  zoning_type: "",
  utilities_available: [],
  road_access: "",
  flood_zone: "",
  annual_taxes: "",
  has_hoa: "",
  gps_coordinates: "",
  land_description: "",
  
  // Step 3 - Property Condition
  property_condition: "",
  major_issues: [],
  recently_updated: "",
  
  // Step 4 - Property Photos
  photos: [],
  video_storage_path: "",
  video_link: "",
  
  // Step 5 - Seller Motivation
  selling_reasons: [],
  timeline: "",
  listed_with_agent: "",
  asking_price: "",
  offer_preferences: [],
  
  // Step 6 - Final Details
  additional_info: "",
  preferred_contact: [],
  best_time_to_contact: [],
  consent_given: false
};

const SellerQuestionnaire = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const recaptchaRef = useRef(null);
  const formContainerRef = useRef(null);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (formContainerRef.current) {
      formContainerRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  // Track ViewContent when questionnaire page loads (runs once on mount)
  useEffect(() => {
    trackViewContent({
      content_name: 'Seller Questionnaire',
      content_category: 'Real Estate Form'
    });
  }, []);

  // Handle text/radio input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "consent_given") {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle checkbox array changes (for multi-select fields)
  const handleCheckboxArrayChange = (arrayName, value) => {
    setFormData(prev => {
      const currentArray = prev[arrayName] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return { ...prev, [arrayName]: newArray };
    });
  };

  // Toggle consent checkbox
  const handleConsentToggle = () => {
    setFormData(prev => ({ ...prev, consent_given: !prev.consent_given }));
  };

  // Handle reCAPTCHA token change
  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  // Handle photos update from PropertyPhotosStep
  const handlePhotosChange = (photoData) => {
    setFormData(prev => ({
      ...prev,
      photos: photoData.photos,
      video_storage_path: photoData.video_storage_path,
      video_link: photoData.video_link
    }));
  };

  // Navigate to next step
  const nextStep = () => {
    if (validateStep(currentStep, formData, recaptchaToken)) {
      setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
    }
  };

  // Navigate to previous step
  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Submit the form
  const handleSubmit = async () => {
    if (!validateStep(6, formData, recaptchaToken)) return;

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire`, {
        ...formData,
        recaptcha_token: recaptchaToken
      });
      
      // Fire Meta Pixel Lead event on successful submission
      trackLead({
        value: formData.asking_price || 0,
        currency: 'USD',
        property_type: formData.property_type,
        city: formData.city,
        state: formData.state,
        timeline: formData.timeline
      });
      
      setSubmitSuccess(true);
      toast.success("Your information has been submitted successfully!");
    } catch (error) {
      logger.error("Error submitting questionnaire:", error);
      const errorMsg = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(errorMsg);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the current step component
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <ContactInfoStep 
            formData={formData} 
            onChange={handleInputChange} 
          />
        );
      case 2:
        return (
          <PropertyInfoStep 
            formData={formData} 
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxArrayChange}
          />
        );
      case 3:
        return (
          <PropertyConditionStep 
            formData={formData} 
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxArrayChange}
          />
        );
      case 4:
        return (
          <PropertyPhotosStep 
            formData={formData}
            onPhotosChange={handlePhotosChange}
            sessionId={sessionId}
          />
        );
      case 5:
        return (
          <SellerMotivationStep 
            formData={formData} 
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxArrayChange}
          />
        );
      case 6:
        return (
          <FinalDetailsStep 
            formData={formData} 
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxArrayChange}
            onConsentToggle={handleConsentToggle}
            recaptchaRef={recaptchaRef}
            recaptchaSiteKey={RECAPTCHA_SITE_KEY}
            onRecaptchaChange={handleRecaptchaChange}
          />
        );
      default:
        return null;
    }
  };

  // Show success screen after submission
  if (submitSuccess) {
    return <SuccessScreen />;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="seller-questionnaire">
      {/* Header */}
      <header className="py-6 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center">
            <Link to="/">
              <img src={KI_LOGO} alt="Knotts Investments LLC" className="h-16 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 sm:py-12" ref={formContainerRef}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Get Your <span className="gold-text">Free Cash Offer</span>
            </h1>
            <p className="text-[#A3A3A3] max-w-xl mx-auto">
              Tell us a little about your property so we can review your situation and contact you with a possible cash offer.
            </p>
          </div>

          {/* Progress Bar */}
          <ProgressBar 
            currentStep={currentStep}
            totalSteps={TOTAL_STEPS}
            stepIcons={STEP_ICONS}
            stepTitles={STEP_TITLES}
          />

          {/* Form Card */}
          <div className="bg-[#141414] border border-white/10 p-6 sm:p-8">
            {renderCurrentStep()}

            {/* Navigation Buttons */}
            <NavigationButtons
              currentStep={currentStep}
              totalSteps={TOTAL_STEPS}
              onPrevious={prevStep}
              onNext={nextStep}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              canSubmit={formData.consent_given && recaptchaToken}
            />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center gap-4 text-sm text-[#A3A3A3] mb-4">
            <Link to="/privacy-policy" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms-conditions" className="hover:text-[#D4AF37] transition-colors">Terms & Conditions</Link>
          </div>
          <p className="text-[#666] text-sm">
            © {new Date().getFullYear()} Knotts Investments LLC. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SellerQuestionnaire;
